import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Spin, Table, Tag, Avatar, Input, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  ScatterChart,
  Scatter
} from 'recharts';
import api from '../api';
import { useFilters } from '../contexts/FilterContext';
import { useTheme } from '../contexts/ThemeContext';
import API_CONFIG from '../config';
import FullscreenChart from './FullscreenChart';
import '../responsive.css';

const { Title, Text } = Typography;

interface CompanyPerformanceProps {
  data: any;
  loading: boolean;
}

const CompanyPerformance: React.FC<CompanyPerformanceProps> = ({ data, loading }) => {
  const { isDark } = useTheme();
  const [topDevelopers, setTopDevelopers] = useState<any[]>([]);
  const [pipelineMetrics, setPipelineMetrics] = useState<any[]>([]);
  const [companyDetails, setCompanyDetails] = useState<any[]>([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [filteredMetrics, setFilteredMetrics] = useState<any[]>([]);
  const [fullscreenChart, setFullscreenChart] = useState<string | null>(null);
  const { filters } = useFilters();

  const fetchCompanyData = React.useCallback(async () => {
    try {
      setChartLoading(true);
      
      // Fetch top developers
      const developersParams = new URLSearchParams({ limit: '15' });
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        developersParams.append('therapeutic_area', filters.therapeuticArea);
      }
      const developersResponse = await api.get(`/api/pipeline/top-developers?${developersParams.toString()}`);
      const developersData = transformBarData(developersResponse.data);
      setTopDevelopers(developersData);

      // Fetch pipeline drugs for detailed analysis
      const params = new URLSearchParams({ status: 'active', limit: '500' });
      
      // Add therapeutic area parameter
      if (filters.therapeuticArea && filters.therapeuticArea !== 'All') {
        params.append('therapeutic_area', filters.therapeuticArea);
      }
      
      // If there are multiple companies selected, add them as developer filter
      if (filters.companySearchMulti && filters.companySearchMulti.length > 0) {
        params.append('developers', filters.companySearchMulti.join(','));
      }
      
      const pipelineResponse = await api.get(`/api/pipeline/drugs?${params.toString()}`);
      console.log(`Pipeline response for ${filters.therapeuticArea}:`, pipelineResponse.data);
      console.log('Number of drugs:', pipelineResponse.data.drugs?.length || 0);
      console.log('Selected companies:', filters.companySearchMulti);
      const companies = analyzePipelineByCompany(pipelineResponse.data.drugs || []);
      console.log('Analyzed companies metrics:', companies.metrics);
      console.log('Companies found in analysis:', companies.metrics.map(c => c.name));
      setPipelineMetrics(companies.metrics);
      setCompanyDetails(companies.details);

    } catch (error) {
      console.error('Error fetching company data:', error);
      // NO MOCK DATA - ONLY REAL DATABASE DATA
      setTopDevelopers([]);
      setPipelineMetrics([]);
      setCompanyDetails([]);
    } finally {
      setChartLoading(false);
    }
  }, [filters.companySearchMulti, filters.therapeuticArea]);

  useEffect(() => {
    if (data) {
      fetchCompanyData();
    }
  }, [data, fetchCompanyData]);

  useEffect(() => {
    // No local filtering needed anymore since we filter at the database level
    setFilteredMetrics(pipelineMetrics);
  }, [pipelineMetrics]);

  const transformBarData = (chartResponse: any) => {
    if (!chartResponse.series || chartResponse.series.length === 0) {
      return [];
    }
    return chartResponse.series[0].data.map((point: any) => ({
      name: point.x,
      value: point.y
    }));
  };

  const analyzePipelineByCompany = (drugs: any[]) => {
    const companyData: any = {};
    
    // If companies are filtered, only analyze the selected companies
    const selectedCompanies = filters.companySearchMulti && filters.companySearchMulti.length > 0 
      ? filters.companySearchMulti 
      : null;
    
    console.log('Analysis - Selected companies:', selectedCompanies);
    
    drugs.forEach(drug => {
      if (!drug.developers || !Array.isArray(drug.developers)) {
        console.warn('Drug has no developers array:', drug);
        return;
      }
      drug.developers.forEach((developer: string) => {
        // If companies are filtered, only process selected companies
        if (selectedCompanies && !selectedCompanies.includes(developer)) {
          console.log(`Skipping developer "${developer}" - not in selected companies`);
          return;
        }
        
        console.log(`Processing developer: "${developer}"`);
        
        if (!companyData[developer]) {
          companyData[developer] = {
            name: developer,
            totalDrugs: 0,
            phaseBreakdown: {},
            classificationBreakdown: {},
            indicationBreakdown: {},
            drugs: []
          };
        }
        
        const company = companyData[developer];
        company.totalDrugs++;
        company.drugs.push(drug);
        
        // Phase breakdown
        company.phaseBreakdown[drug.phase] = (company.phaseBreakdown[drug.phase] || 0) + 1;
        
        // Classification breakdown
        const classification = drug.classification || drug.mechanism || 'Unknown';
        company.classificationBreakdown[classification] = (company.classificationBreakdown[classification] || 0) + 1;
        
        // Indication breakdown
        const indicationTypes = drug.indication_types || drug.indications || [];
        if (Array.isArray(indicationTypes)) {
          indicationTypes.forEach((indication: string) => {
            company.indicationBreakdown[indication] = (company.indicationBreakdown[indication] || 0) + 1;
          });
        }
      });
    });

    // Convert to arrays and calculate metrics
    const companies = Object.values(companyData);
    
    const metrics = companies.map((company: any) => {
      const phaseIII = company.phaseBreakdown['Phase III'] || 0;
      const phaseII = company.phaseBreakdown['Phase II'] || 0;
      const lateStageRatio = company.totalDrugs > 0 ? ((phaseIII + phaseII) / company.totalDrugs) * 100 : 0;
      
      return {
        name: company.name,
        totalDrugs: company.totalDrugs,
        phaseIII,
        phaseII,
        lateStageRatio,
        diversityScore: Object.keys(company.indicationBreakdown).length
      };
    });

    // Sort by total drugs
    metrics.sort((a, b) => b.totalDrugs - a.totalDrugs);
    
    // Debug: Show which companies were found vs selected
    if (selectedCompanies) {
      const foundCompanyNames = metrics.map(m => m.name);
      const missingCompanies = selectedCompanies.filter(name => !foundCompanyNames.includes(name));
      console.log('Selected companies:', selectedCompanies);
      console.log('Found companies:', foundCompanyNames);
      console.log('Missing companies (no drugs found):', missingCompanies);
    }
    
    return {
      metrics,
      details: companies
    };
  };

  // Custom tooltip styling
  const CustomTooltip = ({ active, payload, isDark }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: isDark ? '#1f1f1f' : 'rgba(255, 255, 255, 0.98)',
          border: `1px solid ${isDark ? '#434343' : '#e5e7eb'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          fontSize: '14px',
          fontWeight: 500
        }}>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.65)' : '#6b7280', marginBottom: '4px' }}>{payload[0].name || payload[0].payload.name}</div>
          <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.85)' : '#111827', fontSize: '16px', fontWeight: 600 }}>
            {payload[0].value} {typeof payload[0].value === 'number' && payload[0].dataKey === 'totalDrugs' ? 'drugs' : ''}
            {payload[0].dataKey === 'lateStageRatio' ? '%' : ''}
          </div>
        </div>
      );
    }
    return null;
  };

  // REMOVED ALL MOCK DATA - ONLY REAL DATABASE DATA

  const companyColumns = [
    {
      title: 'Company',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      render: (name: string) => (
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Avatar style={{ backgroundColor: '#1890ff', marginRight: '8px', flex: '0 0 auto' }}>
            {name.charAt(0)}
          </Avatar>
          <Text strong ellipsis={{ tooltip: name }} style={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
            {name}
          </Text>
        </div>
      )
    },
    {
      title: 'Total Drugs',
      dataIndex: 'totalDrugs',
      key: 'totalDrugs',
      width: 110,
      sorter: (a: any, b: any) => a.totalDrugs - b.totalDrugs
    },
    {
      title: 'Phase III',
      dataIndex: 'phaseIII',
      key: 'phaseIII',
      width: 90,
      render: (value: number) => <Tag color="green">{value}</Tag>
    },
    {
      title: 'Phase II',
      dataIndex: 'phaseII',
      key: 'phaseII',
      width: 90,
      render: (value: number) => <Tag color="blue">{value}</Tag>
    },
    {
      title: 'Late-Stage %',
      dataIndex: 'lateStageRatio',
      key: 'lateStageRatio',
      width: 120,
      render: (value: number) => `${value.toFixed(1)}%`,
      sorter: (a: any, b: any) => a.lateStageRatio - b.lateStageRatio
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading company performance...</Text>
        </div>
      </div>
    );
  }

  return (
    <div>

      <Row gutter={[16, 16]}>
        {/* Top Companies by Pipeline Size */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Top Companies by Pipeline Size</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Number of active pipeline drugs
                </Text>
              </div>
            }
            loading={chartLoading}
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
          >
            <FullscreenChart
              chartId="top-companies-pipeline"
              title="Top Companies by Pipeline Size"
              isFullscreen={fullscreenChart === 'top-companies-pipeline'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'top-companies-pipeline' ? null : 'top-companies-pipeline')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {(() => {
                    const topData = (pipelineMetrics)
                      .slice(0, 10)
                      .sort((a: any, b: any) => (b.totalDrugs || b.value) - (a.totalDrugs || a.value));
                    const ellipsize = (text: string, max: number = 22) => {
                      if (!text) return '';
                      return text.length > max ? `${text.slice(0, max - 1)}…` : text;
                    };
                    const longestName = topData.reduce((m: number, d: any) => Math.max(m, (d.name || '').length), 0);
                    // Approximate width based on character count (clamp and assume ellipsis beyond 22 chars)
                    const effectiveChars = Math.min(longestName, 22);
                    const yAxisWidth = Math.min(180, Math.max(100, effectiveChars * 6 + 12));
                    return (
                      <BarChart
                        data={topData}
                        layout="vertical"
                        margin={{ top: 8, right: 12, left: 4, bottom: 8 }}
                        barCategoryGap={'20%'}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis type="number" domain={[0, 'dataMax']} allowDecimals={false} tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={yAxisWidth}
                          interval={0}
                          tickLine={false}
                          axisLine={false}
                          tickMargin={4}
                          tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                          tickFormatter={(v: string) => ellipsize(v)}
                        />
                        <Tooltip content={<CustomTooltip isDark={isDark} />} />
                        <Bar
                          dataKey={'totalDrugs'}
                          fill="#1890ff"
                          barSize={20}
                          radius={[2, 2, 2, 2]}
                        />
                      </BarChart>
                    );
                  })()}
                </ResponsiveContainer>
              }
            >
              {(() => {
                const topData = (pipelineMetrics)
                  .slice(0, 10)
                  .sort((a: any, b: any) => (b.totalDrugs || b.value) - (a.totalDrugs || a.value));
                const ellipsize = (text: string, max: number = 22) => {
                  if (!text) return '';
                  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
                };
                const longestName = topData.reduce((m: number, d: any) => Math.max(m, (d.name || '').length), 0);
                // Approximate width based on character count (clamp and assume ellipsis beyond 22 chars)
                const effectiveChars = Math.min(longestName, 22);
                const yAxisWidth = Math.min(180, Math.max(100, effectiveChars * 6 + 12));
                return (
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart
                      data={topData}
                      layout="vertical"
                      margin={{ top: 8, right: 12, left: 4, bottom: 8 }}
                      barCategoryGap={'20%'}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis type="number" domain={[0, 'dataMax']} allowDecimals={false} tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={yAxisWidth}
                        interval={0}
                        tickLine={false}
                        axisLine={false}
                        tickMargin={4}
                        tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                        tickFormatter={(v: string) => ellipsize(v)}
                      />
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Bar
                        dataKey={'totalDrugs'}
                        fill="#1890ff"
                        barSize={20}
                        radius={[2, 2, 2, 2]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </FullscreenChart>
          </Card>
        </Col>

        {/* Phase Distribution by Top Companies */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Phase Distribution</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Pipeline phases for top companies
                </Text>
              </div>
            }
            loading={chartLoading}
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
          >
            <FullscreenChart
              chartId="phase-distribution-companies"
              title="Phase Distribution"
              isFullscreen={fullscreenChart === 'phase-distribution-companies'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'phase-distribution-companies' ? null : 'phase-distribution-companies')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {(() => {
                    // Filter out companies with no late-stage drugs (Phase II + Phase III = 0)
                    const phaseData = (pipelineMetrics || [])
                      .filter((m: any) => (m.phaseIII || 0) + (m.phaseII || 0) > 0)
                      .slice(0, 5);
                    const ellipsize = (text: string, max: number = 18) => {
                      if (!text) return '';
                      return text.length > max ? `${text.slice(0, max - 1)}…` : text;
                    };
                    return (
                      <BarChart data={phaseData} margin={{ bottom: 45, top: 10, right: 10, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          interval={0}
                          tick={{ fontSize: 11, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                          tickFormatter={(v: string) => ellipsize(v, 22)}
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }} />
                        <Tooltip content={<CustomTooltip isDark={isDark} />} />
                        <Bar dataKey="phaseIII" stackId="a" fill="#52c41a" name="Phase III" />
                        <Bar dataKey="phaseII" stackId="a" fill="#1890ff" name="Phase II" />
                      </BarChart>
                    );
                  })()}
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={300}>
                {(() => {
                  // Filter out companies with no late-stage drugs (Phase II + Phase III = 0)
                  const phaseData = (pipelineMetrics || [])
                    .filter((m: any) => (m.phaseIII || 0) + (m.phaseII || 0) > 0)
                    .slice(0, 5);
                  const ellipsize = (text: string, max: number = 18) => {
                    if (!text) return '';
                    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
                  };
                  return (
                    <BarChart data={phaseData} margin={{ bottom: 45, top: 10, right: 10, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="name" 
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                        tick={{ fontSize: 11, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                        tickFormatter={(v: string) => ellipsize(v, 22)}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }} />
                      <Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Bar dataKey="phaseIII" stackId="a" fill="#52c41a" name="Phase III" />
                      <Bar dataKey="phaseII" stackId="a" fill="#1890ff" name="Phase II" />
                    </BarChart>
                  );
                })()}
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
        </Col>

        {/* Late-Stage Pipeline Ratio */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Late-Stage Pipeline Ratio</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Percentage of Phase II/III drugs
                </Text>
              </div>
            }
            loading={chartLoading}
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
          >
            <FullscreenChart
              chartId="late-stage-ratio"
              title="Late-Stage Pipeline Ratio"
              isFullscreen={fullscreenChart === 'late-stage-ratio'}
              onToggleFullscreen={() => setFullscreenChart(fullscreenChart === 'late-stage-ratio' ? null : 'late-stage-ratio')}
              fullscreenContent={
                <ResponsiveContainer width="100%" height="100%">
                  {(() => {
                    // Filter out companies with no late-stage drugs (Phase II + Phase III = 0)
                    const lateStageData = (pipelineMetrics || [])
                      .filter((m: any) => (m.phaseIII || 0) + (m.phaseII || 0) > 0)
                      .slice(0, 5)
                      .sort((a: any, b: any) => b.lateStageRatio - a.lateStageRatio);
                    const ellipsize = (text: string, max: number = 25) => {
                      if (!text) return '';
                      return text.length > max ? `${text.slice(0, max - 1)}…` : text;
                    };
                    const longestName = lateStageData.reduce((m: number, d: any) => Math.max(m, (d.name || '').length), 0);
                    const effectiveChars = Math.min(longestName, 25);
                    const yAxisWidth = Math.min(200, Math.max(120, effectiveChars * 6 + 12));
                    return (
                      <BarChart
                        data={lateStageData}
                        layout="vertical"
                        margin={{ top: 8, right: 20, left: 4, bottom: 8 }}
                        barCategoryGap={'20%'}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis 
                          type="number" 
                          domain={[0, 100]} 
                          allowDecimals={false}
                          tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                          label={{ value: 'Percentage (%)', position: 'insideBottom', offset: -5, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={yAxisWidth}
                          interval={0}
                          tickLine={false}
                          axisLine={false}
                          tickMargin={4}
                          tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                          tickFormatter={(v: string) => ellipsize(v, 25)}
                        />
<Tooltip content={<CustomTooltip isDark={isDark} />} />
                        <Bar
                          dataKey="lateStageRatio"
                          fill="#52c41a"
                          barSize={20}
                          radius={[2, 2, 2, 2]}
                        />
                      </BarChart>
                    );
                  })()}
                </ResponsiveContainer>
              }
            >
              <ResponsiveContainer width="100%" height={320}>
                {(() => {
                  // Filter out companies with no late-stage drugs (Phase II + Phase III = 0)
                  const lateStageData = (pipelineMetrics || [])
                    .filter((m: any) => (m.phaseIII || 0) + (m.phaseII || 0) > 0)
                    .slice(0, 5)
                    .sort((a: any, b: any) => b.lateStageRatio - a.lateStageRatio);
                  const ellipsize = (text: string, max: number = 25) => {
                    if (!text) return '';
                    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
                  };
                  const longestName = lateStageData.reduce((m: number, d: any) => Math.max(m, (d.name || '').length), 0);
                  const effectiveChars = Math.min(longestName, 25);
                  const yAxisWidth = Math.min(200, Math.max(120, effectiveChars * 6 + 12));
                  return (
                    <BarChart
                      data={lateStageData}
                      layout="vertical"
                      margin={{ top: 8, right: 20, left: 4, bottom: 8 }}
                      barCategoryGap={'20%'}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        type="number" 
                        domain={[0, 100]} 
                        allowDecimals={false}
                        tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                        label={{ value: 'Percentage (%)', position: 'insideBottom', offset: -5, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={yAxisWidth}
                        interval={0}
                        tickLine={false}
                        axisLine={false}
                        tickMargin={4}
                        tick={{ fontSize: 12, fill: isDark ? 'rgba(255, 255, 255, 0.65)' : '#666' }}
                        tickFormatter={(v: string) => ellipsize(v, 25)}
                      />
<Tooltip content={<CustomTooltip isDark={isDark} />} />
                      <Bar
                        dataKey="lateStageRatio"
                        fill="#52c41a"
                        barSize={20}
                        radius={[2, 2, 2, 2]}
                      />
                    </BarChart>
                  );
                })()}
              </ResponsiveContainer>
            </FullscreenChart>
          </Card>
        </Col>

        {/* Company Performance Table */}
        <Col xs={24} sm={24} md={12} lg={12} xl={12}>
          <Card 
            title={
              <div>
                <Title level={5} style={{ margin: 0 }}>Company Performance Metrics</Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Detailed performance analysis
                </Text>
              </div>
            }
            loading={chartLoading}
            style={{ height: 460, display: 'flex', flexDirection: 'column' }}
            headStyle={{ padding: '16px 20px' }}
            bodyStyle={{ padding: '20px' }}
          >
              <Table
                columns={companyColumns}
                dataSource={pipelineMetrics}
              rowKey="name"
              pagination={{ pageSize: 10, showSizeChanger: false, showQuickJumper: true, position: ['bottomRight'] }}
              size="small"
              scroll={{ y: 300 }}
              
            />
          </Card>
        </Col>
      </Row>

      {/* Company Performance Insights */}
      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
              <Card title="Company Performance Insights">
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#1890ff', margin: 0 }}>{
                    pipelineMetrics.length > 0 ? (() => {
                      const leadingCompany = pipelineMetrics.reduce((max: any, company: any) => 
                        (company.totalDrugs || 0) > (max.totalDrugs || 0) ? company : max, pipelineMetrics[0]);
                      return leadingCompany.totalDrugs || 0;
                    })() : '—'
                  }</Title>
                  <Text type="secondary">{
                    pipelineMetrics.length > 0 ? (() => {
                      const leadingCompany = pipelineMetrics.reduce((max: any, company: any) => 
                        (company.totalDrugs || 0) > (max.totalDrugs || 0) ? company : max, pipelineMetrics[0]);
                      return `${leadingCompany.name} Leading Pipeline`;
                    })() : 'Leading Pipeline'
                  }</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#52c41a', margin: 0 }}>{
                    pipelineMetrics.length > 0 ? (
                      `${Math.max(...pipelineMetrics.map((m:any) => m.lateStageRatio)).toFixed(0)}%`
                    ) : '—'
                  }</Title>
                  <Text type="secondary">Best Late-Stage Ratio</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#faad14', margin: 0 }}>{filteredMetrics.length}</Title>
                  <Text type="secondary">Active Companies (filtered)</Text>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ textAlign: 'center', padding: '16px' }}>
                  <Title level={4} style={{ color: '#722ed1', margin: 0 }}>{
                    pipelineMetrics.length > 0 ? (
                      pipelineMetrics.reduce((acc:any,m:any)=>acc+m.totalDrugs,0)
                    ) : '—'
                  }</Title>
                  <Text type="secondary">Total Drugs (filtered)</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CompanyPerformance;